import { useEffect, useRef, useState } from 'react';
import {
  useGlobalStateSelector,
  words,
  stateKeysToSetAddress,
  stateKeysToSetUserName,
} from './globalState';
import { handleClickLogin } from './login';
import { TextLine3 } from './UtilComponents';
import {
  decryptNfcRecords,
  prepareNfcrwScan,
  readAndClearNfcrwCallbackFromLocalStorage,
} from './nativeApp';
import {
  getAddressFromKeyNW,
  getParentAddressFromSubAddress,
  getValidKeyPairs,
  signWithKeyNW,
  walletsAvailable,
  decryptByKeyCardStyle,
  keepKeyPairsInMemory,
  keepKeyPairsAndSubAddressesInMemory,
  getSecretsFromNfc,
  getSecretsFromNfcNativeKeyCard,
  getSecretsFromQr,
} from './keysAndSigning';
import {
  SelectAddressFromKeypoolSection,
  SelectWalletBox,
  SignIcon,
} from './KeysAndSigningUI';
import {
  handleClickRegisterInitial,
  handleClickRegisterInitialFromWif,
  handleClickRegisterAdditional,
  handleClickRegisterAdditionalFromWif,
} from './KeyBox';
import {
  getBalancesMonaparty,
  getAssetInfo,
  getMonacard,
  getAddressInfo,
  getOwnedAssets,
} from './blockchain';
import {
  syncHttpRequest2,
  devLog,
  readNfc,
  decodeNdefRecord,
} from './utils';
import iconCheck from './image/check_24dp_FILL0_wght400_GRAD0_opsz24.svg';
import iconQr from './image/qr_code_2_FILL0_wght400_GRAD0_opsz24.svg';
import iconKeyCard from './image/playing_cards_FILL0_wght400_GRAD0_opsz24.svg';
import iconEdit from './image/edit_FILL0_wght400_GRAD0_opsz48.svg';
import termsAndConditionsUserHtml from './tac_user.static.html';
import privacyPolicyHtml from './privacypolicy.static.link';
import sctHtml from './sct.static.html';

// USER REGISTRATION POPUP ; type: 'userRegistrationPopup'
function UserRegistrationPopup(props) {
  const state = useGlobalStateSelector(({ state }) => state);
  const popupLayer = props.popupLayer;
  const mode = state.popup[popupLayer].mode;

  return <UserRegistration popupLayer={popupLayer} mode={mode} />;
}

// ACCEPTANCE OF TEMS AND CONDITIONS
function AcceptanceOfTermsAndConditions(props) {
  const { state, dispatch } = useGlobalStateSelector(({ state, dispatch }) => ({ state, dispatch }));
  const popupLayer = props.popupLayer;

  if (state.configure.acceptedVersionOfTheTermsAndConditions !== state.config.clientParameters.versionOfTheTermsAndConditions ||
      state.configure.acceptedVersionOfPrivacyPolicy !== state.config.clientParameters.versionOfPrivacyPolicy) {
    return (
      <button className='flexColumn justifyContentCenter alignItemsCenter borderNone backgroundColorWhite heightMin3 textLeft focusEffect01 colorRed cursor borderRadius2 riseOut2 marginTopBottom0p5' tabindex='0'
        onClick={ () => {
            dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'acceptedVersionOfTheTermsAndConditions'], value: state.config.clientParameters.versionOfTheTermsAndConditions });
            dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'acceptedVersionOfPrivacyPolicy'], value: state.config.clientParameters.versionOfPrivacyPolicy });
            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } }); 
        }
      }>
        <div>
          {words.iAcceptTheTermsAndConditionsAndPrivacypolicy[state.language]}
        </div>
      </button>
    );
  }
  else {
    return (
      <button className='borderNone backgroundColorWhite heightMin3 textLeft focusEffect01 borderRadius2 riseOut2 marginTopBottom0p5'
        onClick={ () => {
            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } }); 
        }
      }>
          {words.theTermsAndConditionsAndPrivacypolicyHaveBeenAccepted[state.language]}
      </button>
    );
  }
}


// TERMS AND CONDITIONS POPUP
function TermsAndConditionsPopup(props) {
  const { state, dispatch } = useGlobalStateSelector(({ state, dispatch }) => ({ state, dispatch }));
  const popupLayer = props.popupLayer;

  const termsAndConditions = {
    user: {
      face: words.termsAndConditions[state.language],
      html: termsAndConditionsUserHtml,
    },
    privacyPolicy: {
      face: words.privacyPolicy[state.language],
      html: privacyPolicyHtml,
    },
  };

  const buttons = ['user', 'privacyPolicy'].filter( target1 => target1 !== props.target ).map( target2 =>
    <button className='backgroundColorMonacottoPale borderMonacotto margin0p5'
      onClick={ () => {
        const popup = { type: 'generalItems', body: <TermsAndConditionsPopup target={target2} popupLayer={popupLayer} /> };
        dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: popup });
      }}
    >
      { termsAndConditions[target2].face }
    </button>
  );

  return (
    <div>
      <div>
        { buttons }
      </div>
      <AcceptanceOfTermsAndConditions popupLayer={popupLayer} />
      <div dangerouslySetInnerHTML={ { __html: termsAndConditions[props.target].html } } />
      <AcceptanceOfTermsAndConditions popupLayer={popupLayer} />
    </div>
  );
}

// SCT POPUP
// eslint-disable-next-line no-unused-vars
function SCTPopup(props) {
  return (
    <div dangerouslySetInnerHTML={ { __html: sctHtml } } />
  );
}

// USER REGISTRATION
function UserRegistration(props) {
  const clientParameters = useGlobalStateSelector( ({ state }) => state.config.clientParameters );
  const keyPairsInPoolAll = useGlobalStateSelector( ({ state }) => state.keyPairs );
  const assetInfos = useGlobalStateSelector( ({ state }) => state.assetInfo );
  const monacards = useGlobalStateSelector( ({ state }) => state.monacard );
  const language = useGlobalStateSelector( ({ state }) => state.language || 'english' );
  const paths = useGlobalStateSelector( ({ state }) => state.configKB.clientParameters.paths );
  const walletType = useGlobalStateSelector( ({ state }) => state.walletType );
  const configure = useGlobalStateSelector( ({ state }) => state.configure );
  const login = useGlobalStateSelector( ({ state }) => state.login );
  const session = useGlobalStateSelector( ({ state }) => state.session );
  const marginOfSessionTime = useGlobalStateSelector( ({ state }) => state.marginOfSessionTime );
  const device = useGlobalStateSelector( ({ state }) => state.device );
  const passkey = useGlobalStateSelector( ({ state }) => state.passkey );
  const subAddresses = useGlobalStateSelector( ({ state }) => state.subAddresses );
  const network = useGlobalStateSelector( ({ state }) => state.networkMona );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  const popupLayer = props.popupLayer;
  const popupLayerNext = popupLayer !== undefined && popupLayer !== null ? (popupLayer + 1) : 0;
  const pass = clientParameters.specialMessage;
  const [nfcrwScanLink, setNfcrwScanLink] = useState(null);
  const nfcrwScanSessionRef = useRef(null);
  const expectedNfcrwScanStateRef = useRef(null);
  const nfcrwRegistrationRequestedRef = useRef(false);

  // タップ時に同期的に href を確定できるよう、暗号化スキャン用 Universal Link を事前に組み立てておく
  useEffect( () => {
    if (walletType !== 'nfcNative') {
      return;
    }

    if (!nfcrwScanSessionRef.current) {
      prepareNfcrwScan({ language }).then( ({ session, state, scanLink }) => {
        nfcrwScanSessionRef.current = session;
        expectedNfcrwScanStateRef.current = state;
        setNfcrwScanLink(scanLink);
      });
    }
  }, [walletType, language]);

  // ネイティブアプリからのスキャン結果は、起き上がった元タブが localStorage から自分で読む
  useEffect( () => {
    if (walletType !== 'nfcNative') {
      return;
    }

    const consumeScanCallback = async () => {
      if (!nfcrwRegistrationRequestedRef.current) {
        return;
      }

      const expectedState = expectedNfcrwScanStateRef.current;

      if (!expectedState) {
        return;
      }

      const payload = readAndClearNfcrwCallbackFromLocalStorage({ state: expectedState });

      if (!payload) {
        return;
      }

      const session = nfcrwScanSessionRef.current;

      nfcrwRegistrationRequestedRef.current = false;
      expectedNfcrwScanStateRef.current = null;
      nfcrwScanSessionRef.current = null;
      setNfcrwScanLink(null);

      prepareNfcrwScan({ language }).then( ({ session, state, scanLink }) => {
        nfcrwScanSessionRef.current = session;
        expectedNfcrwScanStateRef.current = state;
        setNfcrwScanLink(scanLink);
      });

      if (payload.error || !payload.pubkey || !payload.ciphertext || !session) {
        dispatch({ type: 'setNotification', key: 'notification', value: words.failedToReadNfc[language] });
        return;
      }

      let records;

      try {
        ({ records } = await decryptNfcRecords({
          keyPair: session.keyPair,
          browserPublicKey: session.browserPublicKey,
          appPublicKeyB64: payload.pubkey,
          ciphertextB64: payload.ciphertext,
          state: expectedState,
        }));
      }
      catch {
        dispatch({ type: 'setNotification', key: 'notification', value: words.failedToReadNfc[language] });
        return;
      }

      try {
        const { mnemonic, keyPairs, addressMain } = await getSecretsFromNfcNativeKeyCard({ dispatch, pass, records, language, network });

        const setupResponse = await handleClickSetup({
          dispatch,
          keyPairs,
          keyPairsInPoolAll,
          assetInfos,
          monacards,
          configure,
          walletType,
          network,
          clientParameters,
          language,
        });

        const sessionToOverride = {
          [addressMain]: {
            sessionId: setupResponse.body.sessionId[addressMain],
            expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
          },
        };

        handleClickLogin({
          dispatch,
          popupLayer,
          keyPairs,
          keyPairsInPoolAll,
          sessionToOverride,
          login,
          walletType,
          session,
          marginOfSessionTime,
          assetInfos,
          monacards,
          network,
          clientParameters,
          language,
        })
        .then( response => {
          dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: response.activeAddress });
        });

        const mnemonicRecords = [{ mnemonic, address: addressMain }];
        keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
      }
      catch {
        return;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        consumeScanCallback();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', consumeScanCallback);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', consumeScanCallback);
    };
  }, [walletType, dispatch, pass, language, network, popupLayer, keyPairsInPoolAll, assetInfos, monacards, configure, login, session, marginOfSessionTime, clientParameters, paths]);

  // const mode = props.mode;
  let registrationSection;

  // アドレスセクション・スイッチアイコン

  let addressSection;

  if (walletType === 'keypool') {
    addressSection =
    <SelectAddressFromKeypoolSection
      stateKeys={['configure', 'addressMain']}
      stateKeysToSetAddress={stateKeysToSetAddress}
      stateKeysToSetUserName={stateKeysToSetUserName}
      loginEnabled={false}
      setConfigure={true}
      subAddressEnabled={true}
      popupLayer={popupLayer}
    />;
  }

  // ユーザー名セクション

  const userNameSection =
  <div className='flexRow justifyContentFlexStart alignItemsCenter '>
    <TextLine3 fieldName='userName' keys={['configure', 'userName']} face={words.userName[language]} tooltip='right' />
  </div>

  // TAC文言

  let tacWord;

  if (configure.acceptedVersionOfTheTermsAndConditions === clientParameters.versionOfTheTermsAndConditions &&
      configure.acceptedVersionOfPrivacyPolicy === clientParameters.versionOfPrivacyPolicy) {
    tacWord = words.theTermsAndConditionsAndPrivacypolicyHaveBeenAccepted[language];
  }
  else {
    tacWord = words.termsAndConditionsAndPrivacypolicy[language];
  }

  // 利用規約ボタン

  const tacButton =
  <button
    className={
      'borderNone backgroundColorWhite heightMin3 textLeft focusEffect01 cursor borderRadius2 riseOut2 marginTopBottom0p5' +
      (configure.readTheTermsAndConditions ? '' : 'colorRed')
    }
    tabindex='0'
    onClick={ () => {
      const popup = {
        type: 'generalItems',
        body: <TermsAndConditionsPopup target='user' popupLayer={ popupLayerNext } />,
      };

      dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
      dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'readTheTermsAndConditions'], value: true });
      dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'readPrivacyPolicy'], value: true });
    }}
  >
    { tacWord }
  </button>;

  // 利用規約同意チェックボックス

  let check;
  let checkboxColor;

  if (
    configure.acceptedVersionOfTheTermsAndConditions === clientParameters.versionOfTheTermsAndConditions &&
    configure.acceptedVersionOfPrivacyPolicy === clientParameters.versionOfPrivacyPolicy
  ) {
    check = <img className='size1x1' src={iconCheck} alt='check' />;
    checkboxColor = 'backgroundColorPinkPale';
  }
  else {
    checkboxColor = 'backgroundColorTransparent';
  }

  const tacCheckbox =
  <button
    className={ 'width1p2 height1p2 borderMonacottoNoRadius padding0 marginSide1 ' + checkboxColor }
    onClick={ () => {
      if (!configure.acceptedVersionOfTheTermsAndConditions || !configure.acceptedVersionOfPrivacyPolicy) {
        dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'acceptedVersionOfTheTermsAndConditions'], value: clientParameters.versionOfTheTermsAndConditions });
        dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'acceptedVersionOfPrivacyPolicy'], value: clientParameters.versionOfPrivacyPolicy });
      }
      else {
        dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'acceptedVersionOfTheTermsAndConditions'], value: null });
        dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'acceptedVersionOfPrivacyPolicy'], value: null });
      }
    }}  
  >
    { check }
  </button>;

  // 登録ボタン

  let registrationButton;
  let mnemonicImportButtonColor = '';
  let wifImportButtonColor = '';

  if (typeof configure.mnemonic === 'string' && configure.mnemonic !== '') {
    mnemonicImportButtonColor = ' backgroundColorPinkPale';
  }

  if (typeof configure.wif === 'string' && configure.wif !== '') {
    wifImportButtonColor = ' backgroundColorPinkPale';
  }

  const webauthnRegistrationButton =
  <>
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      disabled={ passkey.busy }
      onClick={ async () => {
        try {
          dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: true });

          const wifIsSet = typeof configure.wif === 'string' && configure.wif !== '';
          let registrationResult;

          if (wifIsSet) {
            registrationResult = await handleClickRegisterInitialFromWif({
              dispatch,
              wif: configure.wif,
              configure,
              network,
              language,
            });
          }
          else {
            registrationResult = await handleClickRegisterInitial({
              dispatch,
              mnemonic: configure.mnemonic,
              configure,
              network,
              language,
            });
          }

          const { mnemonic, keyPairs, address: addressMain } = registrationResult;

          const setupResponse = await handleClickSetup({
            dispatch,
            keyPairs,
            keyPairsInPoolAll,
            assetInfos,
            monacards,
            configure,
            walletType,
            network,
            clientParameters,
            language,
          });

          const sessionToOverride = {
            [addressMain]: {
              sessionId: setupResponse.body.sessionId[addressMain],
              expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
            },
          };

          handleClickLogin({
            dispatch,
            popupLayer,
            keyPairs,
            keyPairsInPoolAll,
            sessionToOverride,
            login,
            walletType,
            session,
            marginOfSessionTime,
            assetInfos,
            monacards,
            network,
            clientParameters,
            language,
          })
          .then( response => {
            if (wifIsSet) {
              // WIFは階層鍵ではないため親アドレスを持たない
              dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: null });
            }
            else {
              dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: response.activeAddress });
            }
          });

          if (wifIsSet) {
            // WIFは単一鍵のため、ニーモニック由来のサブアドレス展開は行わない
            keepKeyPairsInMemory({
              dispatch,
              keyPairs,
              periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs,
              language,
              network,
            });

            dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'wif'], value: '' });
          }
          else {
            const popup = { type: 'confirmMnemonicPopup', body: { mnemonic } };
            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });

            const mnemonicRecords = [{ mnemonic, address: addressMain }];
            keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
          }
        }
        finally {
          dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });
        }
      }}
    >
      <div>
        {words.initialRegistration[language]}
      </div>
      <SignIcon />
    </button>
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      disabled={ passkey.busy }
      onClick={ async () => {
        try {
          dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: true });

          const wifIsSet = typeof configure.wif === 'string' && configure.wif !== '';
          let registrationResult;

          if (wifIsSet) {
            registrationResult = await handleClickRegisterAdditionalFromWif({
              dispatch,
              wif: configure.wif,
              configure,
              network,
              language,
            });
          }
          else {
            registrationResult = await handleClickRegisterAdditional({
              dispatch,
              mnemonic: configure.mnemonic,
              configure,
              network,
              language,
            });
          }

          const { mnemonic, keyPairs, address: addressMain } = registrationResult;

          const setupResponse = await handleClickSetup({
            dispatch,
            keyPairs,
            keyPairsInPoolAll,
            assetInfos,
            monacards,
            configure,
            walletType,
            network,
            clientParameters,
            language,
          });

          const sessionToOverride = {
            [addressMain]: {
              sessionId: setupResponse.body.sessionId[addressMain],
              expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
            },
          };

          handleClickLogin({
            dispatch,
            popupLayer,
            keyPairs,
            keyPairsInPoolAll,
            sessionToOverride,
            login,
            walletType,
            session,
            marginOfSessionTime,
            assetInfos,
            monacards,
            network,
            clientParameters,
            language,
          })
          .then( response => {
            if (wifIsSet) {
              // WIFは階層鍵ではないため親アドレスを持たない
              dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: null });
            }
            else {
              dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: response.activeAddress });
            }
          });

          if (wifIsSet) {
            // WIFは単一鍵のため、ニーモニック由来のサブアドレス展開は行わない
            keepKeyPairsInMemory({
              dispatch,
              keyPairs,
              periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs,
              language,
              network,
            });

            dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'wif'], value: '' });
          }
          else {
            const popup = { type: 'confirmMnemonicPopup', body: { mnemonic } };
            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });

            const mnemonicRecords = [{ mnemonic, address: addressMain }];
            keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
          }
        }
        finally {
          dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });
        }
      }}
    >
      <div>
        {words.additionalRegistration[language]}
      </div>
      <SignIcon />
    </button>
    <button className={'buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' + mnemonicImportButtonColor} tabindex='0'
      onClick={ () => {
        const popup = {
          type: 'readMnemonicForRegistrationPopup',
        }

        dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
      }}
    >
      { words.importMnemonic[language] }
    </button>
    <button className={'buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' + wifImportButtonColor} tabindex='0'
      onClick={ () => {
        const popup = {
          type: 'readWifForRegistrationPopup',
        }

        dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
      }}
    >
      { words.importWif[language] }
    </button>
  </>;

  if (walletType === 'keybox') {
    registrationButton =
    <div className='flexColumn alignItemsCenter'>
      { webauthnRegistrationButton }
    </div>;
  }
  else if (walletType === 'nfc') {
    registrationButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      onClick={ async () => {
        dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithYourKeyCard[language] });
        const { mnemonic, keyPairs, addressMain } = await getSecretsFromNfc({ dispatch, pass, language, network });

        const setupResponse = await handleClickSetup({
          dispatch,
          keyPairs,
          keyPairsInPoolAll,
          assetInfos,
          monacards,
          configure,
          walletType,
          network,
          clientParameters,
          language,
        });

        const sessionToOverride = {
          [addressMain]: {
            sessionId: setupResponse.body.sessionId[addressMain],
            expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
          },
        };

        handleClickLogin({
          dispatch,
          popupLayer,
          keyPairs,
          keyPairsInPoolAll,
          sessionToOverride,
          login,
          walletType,
          session,
          marginOfSessionTime,
          assetInfos,
          monacards,
          network,
          clientParameters,
          language,
        })
        .then( response => {
          dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: response.activeAddress });
        });

        const mnemonicRecords = [{ mnemonic, address: addressMain }];
        keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
      }}
    >
      <div>
        {words.serviceAccessRegistration[language]}
      </div>
      <SignIcon />
    </button>;
  }
  else if (walletType === 'nfcNative') {
    registrationButton =
    <a className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      href={ nfcrwScanLink }
      onClick={ (event) => {
        if (!nfcrwScanLink) {
          event.preventDefault();
          return;
        }

        nfcrwRegistrationRequestedRef.current = true;
        dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithYourKeyCard[language] });
      }}
    >
      <div>
        {words.serviceAccessRegistration[language]}
      </div>
      <SignIcon />
    </a>;
  }
  else if (walletType === 'qr') {
    registrationButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      onClick={ async () => {
        dispatch({ type: 'setNotification', key: 'notification', value: words.scanYour2DBarcode[language] });
        const { mnemonic, keyPairs, addressMain } = await getSecretsFromQr({ dispatch, pass, popupLayer, language, network });

        const setupResponse = await handleClickSetup({
          dispatch,
          keyPairs,
          keyPairsInPoolAll,
          assetInfos,
          monacards,
          configure,
          walletType,
          network,
          clientParameters,
          language,
        });

        const sessionToOverride = {
          [addressMain]: {
            sessionId: setupResponse.body.sessionId[addressMain],
            expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
          },
        };

        handleClickLogin({
          dispatch,
          popupLayer,
          keyPairs,
          keyPairsInPoolAll,
          sessionToOverride,
          login,
          walletType,
          session,
          marginOfSessionTime,
          assetInfos,
          monacards,
          network,
          clientParameters,
          language,
        })
        .then( response => {
          dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: response.activeAddress });
        });

        const mnemonicRecords = [{ mnemonic, address: addressMain }];
        keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
      }}
    >
      <div>
        {words.serviceAccessRegistration[language]}
      </div>
      <SignIcon />
    </button>;
  }
  else if (walletType === 'keypool') {
    registrationButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      onClick={ async () => {
        const addressMain = configure.addressMain;
        if (addressMain === undefined || addressMain === null || addressMain === '') {
          dispatch({ type: 'setNotification', key: 'notification', value: words.selectAnAddressFromTheKeypool[language] });
          throw new Error('addressMain is not selected');
        }

        const setupResponse = await handleClickSetup({
          dispatch,
          keyPairs: undefined,
          keyPairsInPoolAll,
          assetInfos,
          monacards,
          configure,
          walletType,
          network,
          clientParameters,
          language,
        });

        const sessionToOverride = {
          [addressMain]: {
            sessionId: setupResponse.body.sessionId[addressMain],
            expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
          },
        };

        handleClickLogin({
          dispatch,
          popupLayer,
          keyPairs: null,
          keyPairsInPoolAll,
          sessionToOverride,
          activeAddressCandidate: addressMain,
          login,
          walletType,
          session,
          marginOfSessionTime,
          assetInfos,
          monacards,
          network,
          clientParameters,
          language,
        })
        .then( (loginResponse) => {
          const parentAddress = getParentAddressFromSubAddress({ subAddresses, subAddress: loginResponse?.activeAddress });
          dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: parentAddress });
        });
      }}
    >
      <div>
        {words.serviceAccessRegistration[language]}
      </div>
      <SignIcon />
    </button>;
  }
  else if (walletType === 'mpurse') {
    registrationButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      onClick={ async () => {
        const setupResponse = await handleClickSetup({
          dispatch,
          keyPairs: undefined,
          keyPairsInPoolAll,
          assetInfos,
          monacards,
          configure,
          walletType,
          network,
          clientParameters,
          language,
        });

        const addressMain = Object.keys(setupResponse.body.sessionId)[0];
        const sessionToOverride = {
          [addressMain]: {
            sessionId: setupResponse.body.sessionId[addressMain],
            expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
          },
        };

        handleClickLogin({
          dispatch,
          popupLayer,
          keyPairs: null,
          keyPairsInPoolAll,
          sessionToOverride,
          login,
          walletType,
          session,
          marginOfSessionTime,
          assetInfos,
          monacards,
          network,
          clientParameters,
          language,
        })
        .then( () => {
          dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: null });
        });
      }}
    >
      <div>
        {words.serviceAccessRegistration[language]}
      </div>
      <SignIcon />
    </button>;
  }
  else {
    registrationButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      onClick={ async () => {
        if (walletType === undefined || walletType === null) {
          const walletType = walletsAvailable({ hasCamera: device?.hasCamera })[0];
          dispatch({ type: 'setState', key: 'walletType', value: walletType });
        }

        const result = await handleClickSetup({
          dispatch,
          keyPairs: undefined,
          keyPairsInPoolAll,
          assetInfos,
          monacards,
          configure,
          walletType,
          network,
          clientParameters,
          language,
        });

        if (result?.message === 'nfcRequired') {
          const { mnemonic, keyPairs, addressMain } = await getSecretsFromNfc({ dispatch, pass, language, network });

          const setupResponse = await handleClickSetup({
            dispatch,
            keyPairs,
            keyPairsInPoolAll,
            assetInfos,
            monacards,
            configure,
            walletType,
            network,
            clientParameters,
            language,
          });

          const sessionToOverride = {
            [addressMain]: {
              sessionId: setupResponse.body.sessionId[addressMain],
              expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
            },
          };

          handleClickLogin({
            dispatch,
            popupLayer,
            keyPairs,
            keyPairsInPoolAll,
            sessionToOverride,
            login,
            walletType,
            session,
            marginOfSessionTime,
            assetInfos,
            monacards,
            network,
            clientParameters,
            language,
          })
          .then( response => {
            dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: response.activeAddress });
          });

          const mnemonicRecords = [{ mnemonic, address: addressMain }];
          keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
        }
        else if (result?.message === 'bcRequired') {
          const { mnemonic, keyPairs, addressMain } = await getSecretsFromQr({ dispatch, pass, popupLayer, language, network });

          const setupResponse = await handleClickSetup({
            dispatch,
            keyPairs,
            keyPairsInPoolAll,
            assetInfos,
            monacards,
            configure,
            walletType,
            network,
            clientParameters,
            language,
          });

          const sessionToOverride = {
            [addressMain]: {
              sessionId: setupResponse.body.sessionId[addressMain],
              expirationOfSession: setupResponse.body.expirationOfSession[addressMain],
            },
          };

          handleClickLogin({
            dispatch,
            popupLayer,
            keyPairs,
            keyPairsInPoolAll,
            sessionToOverride,
            login,
            walletType,
            session,
            marginOfSessionTime,
            assetInfos,
            monacards,
            network,
            clientParameters,
            language,
          })
          .then( response => {
            dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: response.activeAddress });
          });

          const mnemonicRecords = [{ mnemonic, address: addressMain }];
          keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
        }
      }}
    >
      <div>
        {words.serviceAccessRegistration[language]}
      </div>
      <SignIcon />
    </button>;
  }

  // 登録セクション

  registrationSection =
  <div>
    <div className="visibleMiddleOrMore flexColumn alignItemsCenter borderKOM marginTop1 padding1">
      <div className=''>
        { words.userRegistration[language] }
      </div>
      <SelectWalletBox keypoolButtonVisible={true} />
      <div className='marginTop0p5 '>
        { addressSection }
      </div>
      <div className='marginTop1 '>
        { userNameSection }
      </div>
      <div className='flexRow justifyContentFlexStart alignItemsCenter marginTopBottom0p5 '>
        { tacCheckbox }
        { tacButton }
      </div>
      { registrationButton }
    </div>
    <div className="visibleSmallOrLess flexColumn alignItemsCenter borderKOM marginTop1 padding1">
      <div className=''>
        { words.userRegistration[language] }
      </div>
      <SelectWalletBox keypoolButtonVisible={true} />
      <div className='marginTop0p5 '>
        { addressSection }
      </div>
      <div className='marginTop1 '>
        { userNameSection }
      </div>
      <div className='flexRow justifyContentFlexStart alignItemsCenter marginTopBottom0p5 '>
        { tacCheckbox }
        { tacButton }
      </div>
      { registrationButton }
    </div>
  </div>;


  return registrationSection;
}

// READ MNEMONIC FOR REGISTRATION POPUP ; type: 'readMnemonicForRegistrationPopup'
function ReadMnemonicForRegistrationPopup(props) {
  const popupLayer = props.popupLayer;
  const popupLayerNext = popupLayer !== undefined && popupLayer !== null ? (popupLayer + 1) : 0;
  const configure = useGlobalStateSelector( ({ state }) => state.configure );
  const hasCamera = useGlobalStateSelector( ({ state }) => state.device?.hasCamera );
  const pass = useGlobalStateSelector( ({ state }) => state.config.clientParameters.specialMessage );
  const language = useGlobalStateSelector( ({ state }) => state.language );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );

  let readButtons = [];

  const nfcButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.readKeyCardNfcMnemonic[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithYourKeyCard[language] });
          const records = await new Promise( (resolve, reject) => {
            readNfc({ dispatch, resolve, reject, language });
          });

          const recordNo = 1;
          const record = records?.[recordNo];

          const encryptedMnemonic = decodeNdefRecord({ dispatch, record, language });
          const mnemonic = await decryptByKeyCardStyle({ pass, dispatch, encrypted: encryptedMnemonic, language });
          dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'mnemonic'], value: mnemonic });
          dispatch({ type: 'setNotification', key: 'notification', value: words.readingDone[language] });
        }}
      >
        <div>
          { words.read[language] }
        </div>
        <div>
          <img className='size2x2' src={iconKeyCard} alt='' />
        </div>
      </button>
    </>;

  const nfcPlainButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.readNfcPlainTextMnemonic[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithNfcTag[language] });
          const records = await new Promise( (resolve, reject) => {
            readNfc({ dispatch, resolve, reject, language });
          });

          const recordNo = 0;
          const record = records?.[recordNo];

          const text = decodeNdefRecord({ dispatch, record, language });
          dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'mnemonic'], value: text });
          dispatch({ type: 'setNotification', key: 'notification', value: words.readingDone[language] });
        }}
      >
        <div>
          { words.read[language] }
        </div>
        <div>
          <img className='size2x2' src={iconKeyCard} alt='' />
        </div>
      </button>
    </>;

  const qrButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.readKeyCardBcMnemonic[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          const encryptedMnemonic = await new Promise( (resolve, reject) => {
            const popup = {
              type: 'qrCodeReaderPopup',
              body: {
                resolve,
                reject,
              },
            };

            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
          });

          const mnemonic = await decryptByKeyCardStyle({ pass, dispatch, encrypted: encryptedMnemonic, language });
          dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'mnemonic'], value: mnemonic });
        }}
      >
        <div>
          { words.read[language] }
        </div>
        <div>
          <img className='size2x2' src={iconQr} alt='' />
        </div>
      </button>
    </>;

  const qrPlainButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.read2dBarcodePlainTextMnemonic[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          const data = await new Promise( (resolve, reject) => {
            const popup = {
              type: 'qrCodeReaderPopup',
              body: {
                resolve,
                reject,
              },
            };

            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
          });

          dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'mnemonic'], value: data });
        }}
      >
        <div>
          { words.read[language] }
        </div>
        <div>
          <img className='size2x2' src={iconQr} alt='' />
        </div>
      </button>
    </>;

  const mnemonicInputButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.inputMnemonicAction[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ () => {
          const popup = {
            type: 'mnemonicInputPopup',
            body: {
              stateKeys: ['configure', 'mnemonic']
            },
          };

          dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
        }}
      >
        <div>
          { words.input[language] }
        </div>
        <div>
          <img className='size2x2' src={iconEdit} alt='' />
        </div>
      </button>
    </>;

  const setMnemonicButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
      onClick={ async () => {
        // dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'mnemonic'], value: configure.mnemonic });
        dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'wif'], value: '' });
        dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } });
      }}
    >
      { words.setMnemonic[language] }
    </button>;

  for (const wallet of walletsAvailable({ hasCamera })) {
    if (wallet === 'nfc') {
      readButtons.push(nfcButton, nfcPlainButton);
    }
    else if (wallet === 'qr') {
      readButtons.push(qrButton, qrPlainButton);
    }
  }

  readButtons.push(mnemonicInputButton);


  return (
    <div className='flexColumn alignItemsCenter borderKOM marginTop1 padding0p5'>
      <div className='flexRow justifyContentSpaceBetween alignItemsCenter widthMax marginTop1 '>
        <div>
          { words.importMnemonic[language] }
        </div>
        <button className='borderNone backgroundColorTransparent cursor borderRadius2 riseOut2 marginSide0p2 ' tabindex='0'
          onClick={ () => dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'mnemonic'], value: null }) }
        >
          {words.clear[language]}
        </button>
      </div>
      <div className='widthMax colorRed marginTop1 '>
        { words.pleaseBeCarefulOfYourSurroundingsSoThatOthersCannotSee[language] }
      </div>
      { readButtons }
      <div className='flexColumn alignItemsFlexStart width90PC borderKOM marginTop1 padding0p5'>
        { configure.mnemonic }
      </div>
      { setMnemonicButton }
    </div>
  );
}

// READ WIF FOR REGISTRATION POPUP ; type: 'readWifForRegistrationPopup'
function ReadWifForRegistrationPopup(props) {
  const popupLayer = props.popupLayer;
  const popupLayerNext = popupLayer !== undefined && popupLayer !== null ? (popupLayer + 1) : 0;
  const configure = useGlobalStateSelector( ({ state }) => state.configure );
  const hasCamera = useGlobalStateSelector( ({ state }) => state.device?.hasCamera );
  const language = useGlobalStateSelector( ({ state }) => state.language );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );

  let readButtons = [];

  const nfcPlainButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.readNfcPlainTextWif[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithNfcTag[language] });
          const records = await new Promise( (resolve, reject) => {
            readNfc({ dispatch, resolve, reject, language });
          });

          const recordNo = 0;
          const record = records?.[recordNo];

          const text = decodeNdefRecord({ dispatch, record, language });
          dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'wif'], value: text });
          dispatch({ type: 'setNotification', key: 'notification', value: words.readingDone[language] });
        }}
      >
        <div>
          { words.read[language] }
        </div>
        <div>
          <img className='size2x2' src={iconKeyCard} alt='' />
        </div>
      </button>
    </>;

  const qrPlainButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.read2dBarcodePlainTextWif[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          const data = await new Promise( (resolve, reject) => {
            const popup = {
              type: 'qrCodeReaderPopup',
              body: {
                resolve,
                reject,
              },
            };

            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
          });

          dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'wif'], value: data });
        }}
      >
        <div>
          { words.read[language] }
        </div>
        <div>
          <img className='size2x2' src={iconQr} alt='' />
        </div>
      </button>
    </>;

  const wifInputButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.inputWifAction[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ () => {
          const popup = {
            type: 'wifInputPopup',
          };

          dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
        }}
      >
        <div>
          { words.input[language] }
        </div>
        <div>
          <img className='size2x2' src={iconEdit} alt='' />
        </div>
      </button>
    </>;

  const setWifButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
      onClick={ () => {
        dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'mnemonic'], value: '' });
        dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } });
      }}
    >
      { words.setWif[language] }
    </button>;

  for (const wallet of walletsAvailable({ hasCamera })) {
    if (wallet === 'nfc') {
      readButtons.push(nfcPlainButton);
    }
    else if (wallet === 'qr') {
      readButtons.push(qrPlainButton);
    }
  }

  readButtons.push(wifInputButton);


  return (
    <div className='flexColumn alignItemsCenter borderKOM marginTop1 padding0p5'>
      <div className='flexRow justifyContentSpaceBetween alignItemsCenter widthMax marginTop1 '>
        <div>
          { words.importWif[language] }
        </div>
        <button className='borderNone backgroundColorTransparent cursor borderRadius2 riseOut2 marginSide0p2 ' tabindex='0'
          onClick={ () => dispatch({ type: 'setStateMultiLayers', keys: ['configure', 'wif'], value: '' }) }
        >
          { words.clear[language] }
        </button>
      </div>
      <div className='widthMax colorRed marginTop1 '>
        { words.pleaseBeCarefulOfYourSurroundingsSoThatOthersCannotSee[language] }
      </div>
      { readButtons }
      <div className='flexColumn alignItemsFlexStart width90PC borderKOM breakAll marginTop1 padding0p5'>
        { configure.wif }
      </div>
      { setWifButton }
    </div>
  );
}

// 「設定」ボタン押下 HANDLE CLICK SETUP
async function handleClickSetup({ dispatch, keyPairs, keyPairsInPoolAll, assetInfos, monacards, configure, walletType, network, clientParameters, language }) {
  let addressMainActual;
  let message;
  let signature;
  let request = {};
  let messages = {};
  let result;
  let keyPair;

  // state固定
  const stateFixed = {
    addressMain: configure.addressMain,
    userName: configure.userName,
    // profileText: state.configure.profileText,
    // imageMain: state.configure.imagesNew.main,
    acceptedVersionOfTheTermsAndConditions: configure.acceptedVersionOfTheTermsAndConditions,
    acceptedVersionOfPrivacyPolicy: configure.acceptedVersionOfPrivacyPolicy,
    signatureVersion: clientParameters.signatureVersion.configure,
    walletType,
    addressCheck: 'off',
  };

  // // 必須項目チェック

  if (stateFixed.userName === undefined || stateFixed.userName === null || stateFixed.userName === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.userNameMustBeFilled[language] });
    return;
  }

  if (stateFixed.acceptedVersionOfTheTermsAndConditions === undefined ||
      stateFixed.acceptedVersionOfTheTermsAndConditions === null ||
      stateFixed.acceptedVersionOfTheTermsAndConditions === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptTheTermsAndConditions[language] });
    return;
  }

  if (stateFixed.acceptedVersionOfPrivacyPolicy === undefined ||
      stateFixed.acceptedVersionOfPrivacyPolicy === null ||
      stateFixed.acceptedVersionOfPrivacyPolicy === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptThePrivacyPolicy[language] });
    return;
  }

  const keyPairsInPool = getValidKeyPairs({ dispatch, keyPairs: keyPairsInPoolAll });

  const clientTime = Date.now();

  if (keyPairs !== undefined && keyPairs !== null) {
    keyPair = keyPairs[0];

    try {
      addressMainActual = getAddressFromKeyNW({ dispatch, keyPair, language, network });
    }
    catch (err) {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[language] });
      return 'rejected';
    }

    // if (stateFixed.addressMain !== undefined && stateFixed.addressMain !== null && stateFixed.addressMain !== '') {
    //   if (!checkIfAddressIsMatchedWithAnAddress({ dispatch, addressActual: addressMainActual, address: stateFixed.addressMain, addressCheck: stateFixed.addressCheck, language })) {
    //     return { status: 'rejected' };
    //   }
    // }
    // else {
      stateFixed.addressMain = addressMainActual;
    //}

    message = `I want to use monashell. My main address is ${stateFixed.addressMain}. My nickname is ${stateFixed.userName}. I accept the terms and conditions version ${stateFixed.acceptedVersionOfTheTermsAndConditions} and privacy policy version ${stateFixed.acceptedVersionOfPrivacyPolicy}. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
    devLog('message', message);

    try {
      signature = signWithKeyNW({ dispatch, keyPair, message, network, language });
    }
    catch (error) {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[language] });
      return 'rejected';
    }
  }
  else if (
    stateFixed.walletType === 'keypool' &&
    stateFixed.addressMain !== undefined && stateFixed.addressMain !== null && stateFixed.addressMain !== '' &&
    keyPairsInPool[stateFixed.addressMain]?.keyPair !== undefined
  ) {
    addressMainActual = stateFixed.addressMain;

    message = `I want to use monashell. My main address is ${stateFixed.addressMain}. My nickname is ${stateFixed.userName}. I accept the terms and conditions version ${stateFixed.acceptedVersionOfTheTermsAndConditions} and privacy policy version ${stateFixed.acceptedVersionOfPrivacyPolicy}. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
    devLog('message', message);

    try {
      signature = signWithKeyNW({ dispatch, keyPair: keyPairsInPool[stateFixed.addressMain].keyPair, message, network, language });
    }
    catch (error) {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[language] });
      return 'rejected';
    }
  }
  else if (stateFixed.walletType === 'mpurse') {
    // Mpurseのアドレスが合ってるかチェック
    result = await window.mpurse.getAddress()
    .then( (result) => {
      addressMainActual = result;

      // if (stateFixed.addressMain !== undefined && stateFixed.addressMain !== null && stateFixed.addressMain !== '') {
      //   if (!checkIfAddressIsMatchedWithAnAddress({ dispatch, addressActual: addressMainActual, address: stateFixed.addressMain, addressCheck: stateFixed.addressCheck, language })) {
      //     return { status: 'rejected' };
      //   }
      // }
      // else {
        stateFixed.addressMain = addressMainActual;
      // }

      message = `I want to use monashell. My main address is ${stateFixed.addressMain}. My nickname is ${stateFixed.userName}. I accept the terms and conditions version ${stateFixed.acceptedVersionOfTheTermsAndConditions} and privacy policy version ${stateFixed.acceptedVersionOfPrivacyPolicy}. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
      devLog('message', message);
    })
    .catch( (error) => {
        dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressInYourWalletCanNotBeGotten[language] });
        return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }

    // 署名
    result = await window.mpurse.signMessage(message)
    .then( result => {
      signature = result;
      return 'fulfilled';
    })
    .catch( err => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[language] });
      return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }
  }
  else {
    if (keyPairs === undefined || keyPairs === null) {
      if (stateFixed.walletType === 'keybox') {
        // dispatch({ type: 'setNotification', key: 'notification', value: words.authenticateWithYourPasskey[language] });
        // return {
        //   status: 'rejected',
        //   message: 'webauthnRequired',
        // };
      }
      else if (stateFixed.walletType === 'nfc') {
        dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithYourKeyCard[language] });
        return {
          status: 'rejected',
          message: 'nfcRequired',
        };
      }
      else if (stateFixed.walletType === 'qr') {
        dispatch({ type: 'setNotification', key: 'notification', value: words.scanYour2DBarcode[language] });
        return {
          status: 'rejected',
          message: 'bcRequired',
        };
      }
    }

    // dispatch({ type: 'setNotification', key: 'notification', value: words.failedToLogin[language] });
    return { status: 'rejected' };
  }

  const formData = new FormData();

  const requestJson = JSON.stringify({
    "body": {
      "addressMain": stateFixed.addressMain,
      "addressMainActual": addressMainActual,
      "userName": stateFixed.userName,
      // "profileText": stateFixed.profileText,
      "acceptedVersionOfTheTermsAndConditions": stateFixed.acceptedVersionOfTheTermsAndConditions,
      "acceptedVersionOfPrivacyPolicy": stateFixed.acceptedVersionOfPrivacyPolicy,
      "clientTime" : clientTime,
      "signature" : signature,
      "signatureVersion" : stateFixed.signatureVersion,
    }
  });

  formData.append('json', requestJson);

  // if (stateFixed.imageMain !== undefined && stateFixed.imageMain !== null) {
  //   formData.append('imageMain', stateFixed.imageMain, stateFixed.imageMain.name);
  // }

  request.body = formData;
  request.headers = {};
  // request.headers = formData.getHeaders();
  // request.headers = {
  //   'Accept': 'application/json',
  //   'Content-Type': 'multipart/form-data; charset=utf-8',
  // };
  request.url = 'https://' + process.env.REACT_APP_MS_API_DOMAIN + '/configure';
  request.method = 'POST';

  messages = {
    "you must accept the terms and conditions of the current version.": words.youMustAcceptTheTermsAndConditionsOfTheCurrentVersion[language],
    "internal server error": words.internalServerError[language],
    "bad signature": words.badSignature[language],
    "bad signature, can't recover address": words.badSignatureCanNotRecoverAddress[language],
    "unknown signatureVersion": words.unknownSignatureVersion[language],
    "clientTime must be larger than last time.": words.clientTimeMustBeLargerThanLastTime[language],
    "this address is not permitted.": words.thisAddressIsNotPermitted[language],
    "this address is forbidden.": words.thisAddressIsForbidden[language],
    "out of service": words.outOfService[language],
    // "you are not delegated.": words.norIsTheSignatureDelegatedFromMainAddress[language],
    // "addressMainActual must be filled.": words.clearYourBrowserOrAppCache[language],
    "success": words.success[language],
  };

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const response = await syncHttpRequest2(request, undefined, undefined, 'notification', messages, dispatch);

  dispatch({ type: 'setState', key: 'accessing', value: false });

  if ( response.status === 'fulfilled' ) {

    // session情報更新
    dispatch({ type: 'setStateMultiLayers', keys: ['session', stateFixed.addressMain],
      value: {'sessionId': response.body.sessionId[stateFixed.addressMain], 'expirationOfSession': response.body.expirationOfSession[stateFixed.addressMain]} });

    /*
    // 基本情報をconfigureにセット
    dispatch({ type: 'setState', key: 'configure', value: response.body[stateFixed.addressMain].basicInformation });
    */

    // 各種stateにメインアドレスをセット
    dispatch({ type: 'setStateMultiLayers', keys: ['login', 'addressMain'], value: stateFixed.addressMain });
    dispatch({ type: 'setStateMultiLayers', keys: ['active', 'addressMain'], value: stateFixed.addressMain });
    dispatch({ type: 'setStateMultiLayers', keys: ['sendMona', 'addressFrom'], value: stateFixed.addressMain });
    dispatch({ type: 'setStateMultiLayers', keys: ['sendXmp', 'addressFrom'], value: stateFixed.addressMain });
    dispatch({ type: 'setStateMultiLayers', keys: ['sendToken', 'addressFrom'], value: stateFixed.addressMain });
    dispatch({ type: 'setStateMultiLayers', keys: ['issuance', 'addressIssuer'], value: stateFixed.addressMain });
    dispatch({ type: 'setStateMultiLayers', keys: ['issuance', 'parent'], value: '' });
    dispatch({ type: 'setStateMultiLayers', keys: ['editAssetInfo', 'addressOwner'], value: stateFixed.addressMain });

    // if (addressMainActual !== stateFixed.addressMain) {
    //   dispatch({ type: 'setStateMultiLayers', keys: ['session', addressMainActual],
    //     value: {'sessionId': response.body.sessionId.addressMainActual, 'expirationOfSession': response.body.expirationOfSession.addressMainActual} });
    // }

    // // cookie情報更新(アドレス情報)
    // const userName = state.usersMonacotto[stateFixed.addressMain] !== undefined ? state.usersMonacotto[stateFixed.addressMain].userName : stateFixed.addressMain;
    // const cookiesNew = [
    //       {
    //         action: 'setUp',
    //         addressType: 'addressMain',
    //         description: `${userName}`,
    //         time: clientTime,
    //         address: stateFixed.addressMain
    //       },
    // ];

    // setAddressesInCookie(state, dispatch, cookies, setCookie, stateFixed.addressMain, cookiesNew);

    // 保有アセット取得
    getBalancesMonaparty({ dispatch, address: stateFixed.addressMain, clientParameters })
    .then( (result) => {
      let assets;

      if (result.status === 'fulfilled') {
        const balance = result.body;
        assets = Object.keys(balance);
      }
      else {
        throw new Error(result.error);
      }

      // アセット情報取得
      return getAssetInfo({ dispatch, assets, assetInfos, clientParameters });
    })
    .then( (result) => {
      let assetInfo;

      if (result.status === 'fulfilled') {
        assetInfo = result.body;
      }
      else {
        throw new Error(result.error);
      }

      // モナカード情報取得
      const assetCommons = Object.keys(assetInfo)
      .filter( asset => {
        try {
          return JSON.parse(assetInfo[asset]?.description).monacard === undefined;
        }
        catch (error) {
          return true;
        }
      })
      .map( asset => assetInfo[asset].asset_longname === null ? asset : assetInfo[asset].asset_longname );

      getMonacard({ dispatch, assets: assetCommons, monacards });
    })
    .catch( (error) => {
      // エラー＆再ログインしてください通知。
    })

    // MONA残高取得

    getAddressInfo({ clientParameters, address: stateFixed.addressMain })
    .then( (result) => {
      if (result.status === 'fulfilled') {
        const balance = result.body.balance;
        dispatch({ type: 'setStateMultiLayers', keys: ['balance', 'mona', stateFixed.addressMain], value: balance });
      }
      else {
        devLog('failed to get address info.');
        // dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSendMona[language] + '3.5' });
      }
    });

    // オウンドアセット情報取得
    getOwnedAssets({ dispatch, address: stateFixed.addressMain, clientParameters })
    .then( (result) => {
      if (result.status === 'fulfilled') {
      }
      else {
        devLog('failed to get owned asset.');
        // dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSendMona[language] + '3.5' });
      }
    });

    // // デッキ取得
    // loadDeck(state, dispatch, { addressMain: stateFixed.addressMain, sessionId: response.body.sessionId.addressMain });
  }
  else {
  }

  return response;
}

export {
  UserRegistrationPopup,
  UserRegistration,
  ReadMnemonicForRegistrationPopup,
  ReadWifForRegistrationPopup,
};
